/* tslint:disable */
import './InvoiceEditor.scss';
import * as React from 'react';
import { defaultLine } from '../../actions/helpers/draft';
import { Container } from '../../Global/Container';
import { DraftInterface } from '../../Interfaces/Draft/DraftInterface';
import { InvoiceItemType } from '../../Interfaces/Draft/DraftItemType';
import { FieldInterface } from '../../Interfaces/Draft/FieldInterface';
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface';
import { LineInterface } from '../../Interfaces/Draft/LineInterface';
import { TextLineInterface } from '../../Interfaces/Draft/TextInterface';
import { ExchangeRateInterface } from '../../Interfaces/ExchangeRateInterface';
import { LabelKeys } from '../../Interfaces/LabelsInterface';
import { SchemaInterface } from '../../Interfaces/Schema/SchemaInterface';
import { ModelInterface } from '../../Interfaces/Settings/ModelInterface';
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface';
import { convertVatpctFromLocale } from '../../Other/functions/vatFunctions';
import { l, tsxl } from '../../Other/Translation';
import { Button, ButtonType } from '../common/Button/Button';
import { Document } from '../common/Document/Document';
import { FieldCreator } from '../common/FieldCreator';
import { Layout, LayoutColumn } from '../common/Layout/Layout';
import { NumberInput } from '../common/NumberInput';
import { InvoiceEditorHeading } from './InvoiceEditorHeading/InvoiceEditorHeading';
import { InvoiceHeader } from './InvoiceHeader/InvoiceHeader';
import { InvoiceItems } from './InvoiceItems';
import { SubtotalLine } from './SubtotalLine';
import { InvoiceTextLine } from './TextLine';
import { InvoiceTitleLine } from './TitleLine';
import { Input } from '../common/Input/Input';
import { NumberFormatter } from '../../Other/Format/NumberFormatter';
import { Format } from '../../Other/Format/Format';
import { PercentageFormatter } from '../../Other/Format/PercentageFormatter';
import { CustomerDetails } from './CustomerDetails';
import { CompanyDetails } from './CompanyDetails';
import { PaymentMethods } from './PaymentMethods'
import { PaymentMeanInterface } from '../../Interfaces/Settings/PaymentMeanInterface';

export interface InvoiceEditorPropsInterface {
  draft?: DraftInterface;
  settings?: SettingsInterface;
  language?: string;
  locale?: string;
  config?: any;
  automation?: string;
  company?: any;
  selectMaps?: {
    paymentMethods: { [key: string]: string };
    currencies: { [key: string]: string };
    languages: { [key: string]: string };
  };
  internal?: {
    model: ModelInterface;
    language: string;
    template: string;
    visibleColumns: string[];
    lineColumns: string[];
    schema: SchemaInterface;
    currencySymbol: string;
    defaultCurrency: string;
  };
  changeFields?: (fields: DraftFieldsInterface) => void;
  changeLines?: (lines: InvoiceItemType[]) => void;
  changeDraft?: (draft: DraftInterface) => void;
  previewDraft?: (draft: DraftInterface) => void;
  exchangeRate?: ExchangeRateInterface;
  openPdfWindow: (draft) => void;
  downloadPDF: (draft) => void;
}

export interface OwnStateInterface {
  isCompanyModalOpen: boolean;
  isPreviewLoading: boolean;
  isDownloading: boolean;
}

export class InvoiceEditor extends Container<InvoiceEditorPropsInterface & OwnStateInterface, OwnStateInterface> {
  private currencyF: NumberFormatter;

  constructor(props) {
    super(props);

    this.state = {
      isCompanyModalOpen: false,
      isPreviewLoading: false,
      isDownloading: false
    };
    this.currencyF = new NumberFormatter(Format.fromLocale(this.props.locale), props.internal.currencySymbol)
      ;
  }
  private savePaymentMeans = (paymentMeans: PaymentMeanInterface[]) => {
    this.props.changeDraft({ ...this.props.draft, paymentMeans })
  };

  private renderPaymentMethods = (draft: DraftInterface) => {
    const heading = <div className="Label">{l('PaymentMethods')}</div>;
    const paymentMeans = draft.paymentMeans || [];
    return (
      <div id="paymentMeansInput" className="PaymentMeans">
        {heading}
        <PaymentMethods paymentMeans={paymentMeans} savePaymentMeans={this.savePaymentMeans} />
      </div>
    );
  };

  private renderCompanyInfo = () => {
    let companyName = this.props.draft.invoicer ? this.props.draft.invoicer.tradeName.name : '';

    if (this.props.draft && companyName) {
      return (
        <div>{companyName}</div>
      )
    } else {
      return (
        <Input
          editable={true}
          value={companyName}
          key={`company-info`}
        />

      )
    }
  };

  public render(): JSX.Element {
    const DRAFT = this.props.draft;
    if (DRAFT === null || DRAFT === undefined || DRAFT.fields === undefined) {
      return null;
    }
    const FIELDS = DRAFT.fields;
    if (!FIELDS) {
      return <h1>Loading invoice</h1>;
    }

    let discountFormatter: NumberFormatter;
    if (FIELDS.discountType.value === 'amount') {
      discountFormatter = new NumberFormatter(
        Format.fromLocale(this.props.locale),
        this.props.internal.currencySymbol
      );
    } else {
      discountFormatter = new PercentageFormatter(Format.fromLocale(FIELDS.locale.value), true);
    }

    const labels = LabelKeys['invoice'];
    const editorContainerClass = 'editorContent';
    const totalLabel = 'ToBePaid';
    let language = this.props.internal.language;
    if (language === 'gb') { language = 'en' }
    return (
      <div className="ReactEditor" data-automation={this.props.automation} style={{ height: '100%' }}>
        <InvoiceEditorHeading
          branding={l('generator.branding')}
          brandingDetails={l('generator.branding.description')}
          logoLinkUrl={`https://zervant.com/${language}`}
        >
          <Button
            label={l(labels.buttons.next)}
            automation="header-button-next"
            type={ButtonType.normal}
            showLoadingAnimation={this.state.isPreviewLoading}
            action={async () => {
              this.setState({ isPreviewLoading: true });
              await this.props.previewDraft(this.props.draft);
              this.setState({ isPreviewLoading: false });
            }}
          />
          <Button
            label={l(labels.buttons.pdf)}
            automation="header-button-pdf"
            type={ButtonType.accept}
            showLoadingAnimation={this.state.isDownloading}
            action={async () => {
              this.setState({ isDownloading: true });
              await this.props.downloadPDF(this.props.draft)
              this.setState({ isDownloading: false });
            }}
          />
        </InvoiceEditorHeading>
        <div className={editorContainerClass}>
          <Document automation={this.props.automation} className="InvoiceEditor">
            <h1 className="title">
              {FIELDS.title.value}<br />
              {
                this.props.draft.invoicer &&
                this.props.draft.invoicer.tradeName.logo.content &&
                <img src={this.props.draft.invoicer.tradeName.logo.content} alt="logo"></img>
              }

            </h1>
            <CustomerDetails
              customer={this.props.draft.receiver}
              saveCustomer={(receiver) => this.props.changeDraft({ ...this.props.draft, receiver })}
            />
            <CompanyDetails
              invoicer={this.props.draft.invoicer}
              saveCompany={(invoicer) => {
                this.props.changeDraft({ ...this.props.draft, invoicer })
              }}
            />
            <hr />
            <InvoiceHeader
              onChange={(field: FieldInterface) => {
                this.props.draft.fields[field.name.toString()] = field;
                this.props.changeFields({ ...this.props.draft.fields });
              }}
              settings={this.props.settings}
              model={this.props.internal.model}
              schema={this.props.internal.schema}
              fields={this.props.draft.fields}
              invalidFields={this.props.draft.invalidFields}
              automation={'invoiceHeader'}
              locale={this.props.locale}
              country={'FR'}
            />
            <InvoiceItems
              lines={DRAFT.lines}
              settings={this.props.settings}
              fields={this.props.draft.fields}
              columns={this.props.internal.lineColumns as Array<keyof LineInterface>}
              onChange={(values) => {
                this.props.changeLines(values);
              }}
              locale={this.props.locale}
              currencySymbol={this.props.internal.currencySymbol}
            />

            <Layout>
              <LayoutColumn style={{ textAlign: 'left', padding: '0px' }}>
                <Button
                  className="addItemButton"
                  label={tsxl('New')}
                  action={() => {
                    this.props.draft.lines.push(defaultLine(this.props.settings));
                    this.props.changeLines(this.props.draft.lines);
                  }}
                  automation="invoiceItem-addLine"
                >
                  <Button
                    label={tsxl('New')}
                    action={() => {
                      this.props.draft.lines.push(defaultLine(this.props.settings));
                      this.props.changeLines(this.props.draft.lines);
                    }}
                    automation="invoiceItem-addLine"
                  />
                  <Button
                    label={tsxl('invoice.add.Subtitle')}
                    action={() => {
                      this.props.draft.lines.push(InvoiceTitleLine.defaultProps);
                      this.props.changeLines(this.props.draft.lines);
                    }}
                    automation="invoiceItem-addTitle"
                  />
                  <Button
                    label={tsxl('invoice.add.Description')}
                    action={() => {
                      // @ts-ignore
                      const o: TextLineInterface = {
                        ...InvoiceTextLine.defaultProps,
                        content: '',
                        type: 'description'
                      };
                      this.props.draft.lines.push(o);
                      this.props.changeLines(this.props.draft.lines);
                    }}
                    automation="invoiceItem-addDescription"
                  />
                  <Button
                    label={tsxl('invoice.add.Subtotal')}
                    action={() => {
                      const { lineColumns } = this.props.internal;
                      const isNetTotal = lineColumns.indexOf('netTotal') >= 0;
                      const isTaxTotal = lineColumns.indexOf('taxTotal') >= 0;
                      this.props.draft.lines.push(SubtotalLine.generateDefaultProps(isNetTotal, isTaxTotal));
                      this.props.changeLines(this.props.draft.lines);
                    }}
                    automation="invoiceItem-addSubtotal"
                  />
                </Button>
              </LayoutColumn>
              <LayoutColumn style={{ flexBasis: '300px', flexGrow: 0 }}>
                <div className="sumArea">
                  {FIELDS.discountEnabled.value && FIELDS.discountLocation.value === 'perTotal' && (
                    <NumberInput
                      decimals={2}
                      key={'amount'}
                      formatter={discountFormatter}
                      automation={this.props.automation + '-discount'}
                      value={FIELDS.discountAmount.value}
                      label={tsxl('DiscountGiven', this.props.draft.document)}
                      onBlur={(value) => {
                        this.props.changeFields({
                          ...FIELDS,
                          discountAmount: { name: 'discountAmount', value }
                        });
                      }}
                    />
                  )}
                  <NumberInput
                    decimals={2}
                    editable={false}
                    borderless={true}
                    value={DRAFT.summary.netTotal}
                    automation={this.props.automation + '-net'}
                    formatter={this.currencyF}
                    label={tsxl('invoice.totalAmount')}
                  />
                  {DRAFT.summary.taxSummary.map((vat) => {
                    return (
                      vat.percentage > 0 && (
                        <NumberInput
                          decimals={2} // do not show VAT 0% as it is always 0
                          formatter={this.currencyF}
                          editable={false}
                          key={'vat' + vat.percentage}
                          automation={this.props.automation + '-vat-' + vat.percentage}
                          borderless={true}
                          label={
                            l('Tax') + ' ' + convertVatpctFromLocale(vat.percentage, this.props.locale)
                          }
                          value={vat.amount}
                        />
                      )
                    );
                  })}

                  <NumberInput
                    decimals={2}
                    borderless={true}
                    editable={false}
                    value={DRAFT.summary.taxTotal}
                    automation={this.props.automation + '-vat'}
                    formatter={this.currencyF}
                    label={tsxl('invoice.VatTotal')}
                  />

                  {FIELDS.discountEnabled.value && (
                    <NumberInput
                      decimals={2}
                      borderless={true}
                      editable={false}
                      formatter={this.currencyF}
                      value={this.props.draft.summary.discountTotal}
                      automation={this.props.automation + '-discountGiven'}
                      label={tsxl('invoice.DiscountGiven')}
                    />
                  )}
                  <hr className="full" />
                  <div className="total">
                    <NumberInput
                      decimals={2}
                      borderless={true}
                      editable={false}
                      value={DRAFT.summary.grossTotal}
                      automation={this.props.automation + '-total'}
                      formatter={this.currencyF}
                      label={tsxl(totalLabel)}
                    />
                  </div>
                </div>
              </LayoutColumn>
            </Layout>
            <hr />
            <div className="footer">
              <Layout>
                <LayoutColumn>
                  <FieldCreator
                    name="footerText"
                    className={"editorFooter"}
                    model={this.props.internal.model}
                    schema={this.props.internal.schema}
                    translator={tsxl}
                    props={{
                      value: FIELDS.footerText ? FIELDS.footerText.value : '',
                      labelPosition: 'top',
                      automation: this.props.automation + '-footnote',
                      invalid:
                        this.props.draft.invalidFields && this.props.draft.invalidFields.indexOf('footerText') >= 0,
                      onBlur: (value: string) => {
                        this.props.changeFields({
                          ...FIELDS,
                          footerText: { ...FIELDS.footerText, value }
                        });
                      },
                      maxLength: 250,
                      showCounter: true
                    }}
                  />
                </LayoutColumn>
                <LayoutColumn>
                  {this.renderPaymentMethods(DRAFT)}
                </LayoutColumn>
              </Layout>
            </div>
          </Document>
        </div>
      </div>
    );
  }
}
